"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { MotionConfig, motion, type Variants } from "motion/react";
import { GlassCard } from "@/components/ui/GlassCard";
import { ComingSoonFooter } from "./ComingSoonFooter";
import { ComingSoonHeader } from "./ComingSoonHeader";
import { routes } from "@/lib/links";

export type LegalSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

type LegalPageProps = {
  title: string;
  lastUpdated: string;
  lead: string;
  sections: LegalSection[];
};

const containerStagger: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.15 },
  },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Layout shell for the policy pages. Renders the entity attribution line,
 * a numbered list of sections (h2 + paragraphs + optional bullet list), and
 * the shared coming-soon header/footer.
 */
export function LegalPage({ title, lastUpdated, lead, sections }: LegalPageProps) {
  return (
    <MotionConfig reducedMotion="user">
      <ComingSoonHeader />
      <main className="relative px-4 pt-8 pb-16 sm:pt-14 sm:pb-20">
        <motion.div
          variants={containerStagger}
          initial="hidden"
          animate="show"
          className="mx-auto max-w-2xl"
        >
          <motion.div variants={fadeUp}>
            <Link
              href={routes.home}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-sm font-medium text-neutral-700 transition-all hover:-translate-y-0.5 hover:text-neutral-900 active:scale-95"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.2} />
              Back to home
            </Link>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-8">
          <GlassCard className="p-5 sm:p-12" interactive={false}>
            <h1 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
              {title}
            </h1>
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-neutral-600">
              Last updated · {lastUpdated}
            </p>

            <p className="mt-6 text-balance text-neutral-700 sm:text-lg">
              {lead}
            </p>

            <div className="mt-10 space-y-8 text-neutral-700">
              {sections.map((s, i) => (
                <section key={s.heading} aria-labelledby={`s-${i}`}>
                  <h2
                    id={`s-${i}`}
                    className="text-base font-semibold tracking-tight text-neutral-900 sm:text-lg"
                  >
                    <span className="mr-2 font-mono text-sm text-neutral-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.heading}
                  </h2>
                  {s.paragraphs?.map((p, j) => (
                    <p
                      key={j}
                      className="mt-3 text-sm leading-relaxed text-neutral-700 sm:text-base"
                    >
                      {p}
                    </p>
                  ))}
                  {s.bullets && (
                    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-neutral-700 marker:text-neutral-400 sm:text-base">
                      {s.bullets.map((b, k) => (
                        <li key={k}>{b}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            <p className="mt-10 border-t border-neutral-200 pt-6 text-xs text-neutral-600">
              DET E-Trader License No. 1626932 · Dubai Unified License No. FC3978
              · Issued by the Department of Economy and Tourism, Government of
              Dubai.
            </p>
          </GlassCard>
          </motion.div>
        </motion.div>
      </main>
      <ComingSoonFooter />
    </MotionConfig>
  );
}
